import { Classes, Colors, InputGroup } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
  ButtonContainer,
  FGCustomInput,
  FGEmpty,
  FGSelectInput,
  FGTextInput,
  FieldGroup,
  Loading,
  showError,
  showSuccess,
  useApiEffect,
} from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import * as Yup from "yup";

import { DogDnaApi, EIsagNorm } from "../../../../../api";
import { DeleteButton, FGSRSHCheckboxInput, SmallFormGenerator } from "../../../../../components";
import { useDialog, useEventsContext } from "../../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IDogDnaPageProps {
  dogId: number;
  editMode: boolean;
  onSave?: (id: number) => void;
  refetchEditMode: () => void;
  isag: EIsagNorm;
}

const ViewInput = styled(InputGroup)<{ colorcheck: string }>`
  & .${Classes.ICON} {
    color: ${(p) => p.colorcheck};
    margin-left: 0;
  }
`;

export const DogDnaPage: React.FunctionComponent<IDogDnaPageProps> = ({
  dogId,
  editMode,
  onSave,
  refetchEditMode,
  isag,
}) => {
  const { t } = useTl();
  const { dispatchEvent } = useEventsContext();
  const api = useApiService(DogDnaApi);
  const [loadingdeleteAllDna, setloadingDeleteAllDna] = React.useState(false);
  const [loadingDeleteDnaProfile, setloadingDeleteDnaProfile] = React.useState(false);
  const { showDialogPromise } = useDialog();
  const [dnaDocInshp, dnaDocInshpLoading] = useApiEffect(() => api.isDogInShp(dogId), [dogId]);
  const [labs, lloading] = useReferential((a) => a.getLabNames());
  const save = React.useCallback(
    async (d) => {
      d.isag = isag;
      var res = await api.saveDnaResult(d);
      if (!!onSave) {
        onSave(res);
      }

      return res;
    },
    [api, onSave, isag]
  );
  const { data, loading, saving, saveItem, refresh } = useCrudApi(
    {
      getApiFn: () => api.getDnaResult(dogId, isag),
      saveApiFn: (d) => save(d),
      deleteApiFn: (d) => {},
    },
    [dogId]
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      resultIdentifier: Yup.string().nullable().required(t(ETLCodes.Required)),
      labId: Yup.number().min(1, t(ETLCodes.Required)).nullable().required(t(ETLCodes.Required)),
    });
  }, [t]);

  const deleteAllDna = React.useCallback(async () => {
    setloadingDeleteAllDna(true);
    var dialogResult = await showDialogPromise({
      message: t(ETLCodes.DeleteAllDnaConfirmationMessage),
      title: t(ETLCodes.Warning),
    });
    if (dialogResult !== "yes") {
      setloadingDeleteAllDna(false);
      return;
    }
    try {
      await api.deleteAllDogDna(dogId);
      dispatchEvent("RefreshSearchTables");
      showSuccess(t(ETLCodes.Success));
    } catch {
      showError(t(ETLCodes.Error));
    }
    setloadingDeleteAllDna(false);
  }, [api, dispatchEvent, dogId, showDialogPromise, t]);

  const deleteDnaProfile = React.useCallback(async () => {
    setloadingDeleteDnaProfile(true);
    let hasOpenBroodRequest = false;
    try {
      hasOpenBroodRequest = await api.dogHasOpenBroodRequest(dogId);
    } catch (err) {
      console.error(err);
    }
    var dialogResult = await showDialogPromise({
      message: hasOpenBroodRequest
        ? t(ETLCodes.OpenBroodRequestExistsForThisDogDoYouStillWantTodeleteDnaProfile)
        : t(ETLCodes.DeleteDnaProfileConfirmationMessage),
      title: t(ETLCodes.Warning),
    });
    if (dialogResult !== "yes") {
      setloadingDeleteDnaProfile(false);
      return;
    }
    try {
      await api.deleteDogDnaProfile(dogId);
      dispatchEvent("RefreshSearchTables");
      showSuccess(t(ETLCodes.Success));
      refresh();
      refetchEditMode();
    } catch {
      showError(t(ETLCodes.Error));
    }
    setloadingDeleteDnaProfile(false);
  }, [api, dispatchEvent, dogId, showDialogPromise, t, refresh]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        validationSchema={FormSchema}
        loading={loading}
        showDeleteButton={false}
        saving={saving}
        hideButtons={!editMode}
      >
        <FieldGroup columns={2}>
          <FGTextInput name="resultIdentifier" label={t(ETLCodes.ResultIdentifier)} maxLength={50} />
          <FGEmpty />
          <FGSRSHCheckboxInput name="controlledFiliation" label={t(ETLCodes.FiliationControlled)} />
          <FGSRSHCheckboxInput name="correctFiliation" label={t(ETLCodes.FiliationCorrect)} />

          <FGCustomInput label={t(ETLCodes.SharePointPdfAvailable)}>
            {(ctx) => (
              <Loading isLoading={dnaDocInshpLoading}>
                {dnaDocInshp?.value ? (
                  <ViewInput
                    leftIcon={IconNames.TICK}
                    readOnly={true}
                    style={{
                      boxShadow: "none",
                      backgroundColor: "transparent",
                    }}
                    colorcheck={Colors.GREEN5}
                  ></ViewInput>
                ) : (
                  <ViewInput
                    leftIcon={IconNames.CROSS}
                    readOnly={true}
                    style={{
                      boxShadow: "none",
                      backgroundColor: "transparent",
                    }}
                    colorcheck={Colors.RED5}
                  ></ViewInput>
                )}
              </Loading>
            )}
          </FGCustomInput>
          <FGSRSHCheckboxInput name="sharePointPdfAvailable" readonly />
          <FGSelectInput name="labId" label={t(ETLCodes.Laboratory)} items={labs} loading={lloading} />
          <ButtonContainer>
            {" "}
            <DeleteButton
              text={t(ETLCodes.DeleteAllDna)}
              onDelete={deleteAllDna}
              loading={loadingdeleteAllDna}
              minimal={false}
            />
            <div style={{ width: "1rem" }}></div>
            <DeleteButton
              text={t(ETLCodes.DeleteDnaProfile)}
              onDelete={deleteDnaProfile}
              loading={loadingDeleteDnaProfile}
              minimal={false}
            />
          </ButtonContainer>
        </FieldGroup>
      </SmallFormGenerator>
    </>
  );
};
